import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, Button, MenuItem, Paper, Skeleton, Snackbar, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Typography, FormControlLabel,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip,
  CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  fetchArticles, fetchArticleDetail, fetchSuppliers,
  ArticleListItem, ArticleDetail, SupplierInfo, apiErrorText } from '../api/client';
import {
  InvoicePdfButton, NichtNormalisiertChip, ProvenanceTooltip, fmtEURUnit, fmtQty,
} from '../components/LineProvenance';

function fmtEUR(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function fmtDate(d?: string | null): string {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('de-DE'); } catch { return d; }
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || isNaN(n as number)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'spend_desc', label: 'Gesamtausgaben (absteigend)' },
  { value: 'name_asc', label: 'Name (A–Z)' },
  { value: 'recent_desc', label: 'Zuletzt gesehen' },
  { value: 'delta_desc', label: 'Δ vs Min (absteigend)' },
];

export default function ArtikelSuchePage() {
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get('q') || '';
  const initialSuppliers = searchParams.get('suppliers');

  // Filter state — seed from URL if present (e.g. coming from LieferantenPage)
  const [q, setQ] = useState(initialQ);
  const [debouncedQ, setDebouncedQ] = useState(initialQ);
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>(
    initialSuppliers ? initialSuppliers.split(',').filter(Boolean) : []
  );
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [onlyAlerts, setOnlyAlerts] = useState(false);
  const [sort, setSort] = useState('spend_desc');
  const [page, setPage] = useState(0); // 0-based for MUI TablePagination
  const [pageSize, setPageSize] = useState(50);

  // Results state
  const [items, setItems] = useState<ArticleListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detail dialog state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<ArticleDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [pdfNotice, setPdfNotice] = useState<string | null>(null);

  // Debounce search input
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQ(q);
      setPage(0);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q]);

  // Load suppliers on mount
  useEffect(() => {
    (async () => {
      try {
        const data: SupplierInfo[] = await fetchSuppliers();
        const names = Array.from(
          new Set(data.map(s => s.name).filter((n): n is string => !!n)),
        ).sort((a, b) => a.localeCompare(b, 'de'));
        setSupplierOptions(names);
      } catch {
        // non-fatal: filter just won't have suggestions
      }
    })();
  }, []);

  // Reset to page 0 whenever non-search filters change
  useEffect(() => { setPage(0); }, [selectedSuppliers, dateFrom, dateTo, onlyAlerts, sort, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchArticles({
        q: debouncedQ || undefined,
        suppliers: selectedSuppliers.length ? selectedSuppliers.join(',') : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        only_alerts: onlyAlerts || undefined,
        sort,
        page: page + 1,
        page_size: pageSize,
      });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e: any) {
      setError(apiErrorText(e) || e?.message || 'Fehler beim Laden der Artikel');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, selectedSuppliers, dateFrom, dateTo, onlyAlerts, sort, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (item: ArticleListItem) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const d = await fetchArticleDetail(item.supplier, item.article_no);
      setDetail(d);
    } catch (e: any) {
      setDetailError(apiErrorText(e) || e?.message || 'Detail konnte nicht geladen werden');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetail(null);
    setDetailError(null);
  };

  const chartData = useMemo(() => {
    if (!detail?.history) return [];
    return [...detail.history]
      .filter(p => !!p.date)
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
      .map(p => ({
        date: p.date,
        label: fmtDate(p.date),
        unit_price: p.unit_price,
        quantity: p.quantity,
      }));
  }, [detail]);

  // Every invoice line behind the chart, newest first. Each point carries its
  // own provenance (raw line, pack read, invoice) so a price can be traced.
  const lineRows = useMemo(() => {
    if (!detail?.history) return [];
    return [...detail.history].sort((a, b) => {
      const da = a.date || '', db = b.date || '';
      return da < db ? 1 : da > db ? -1 : 0;
    });
  }, [detail]);

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Artikel
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Suche und Vergleich aller Artikel über alle Lieferanten.
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 2fr 1fr 1fr' },
            gap: 2,
            alignItems: 'center',
          }}>
            <TextField
              size="small"
              label="Suche (Artikelname oder Art-Nr.)"
              value={q}
              onChange={e => setQ(e.target.value)}
              fullWidth
            />
            <Autocomplete
              multiple
              size="small"
              options={supplierOptions}
              value={selectedSuppliers}
              onChange={(_, v) => setSelectedSuppliers(v)}
              renderInput={(params) => <TextField {...params} label="Lieferant" placeholder="Auswählen…" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" size="small" label={option} {...getTagProps({ index })} />
                ))
              }
            />
            <TextField
              size="small"
              type="date"
              label="Von"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label="Bis"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
          </Box>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mt: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={onlyAlerts}
                  onChange={(_, v) => setOnlyAlerts(v)}
                />
              }
              label="Nur mit Warnungen"
            />
            <TextField
              size="small"
              select
              label="Sortierung"
              value={sort}
              onChange={e => setSort(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              {SORT_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Results */}
      {loading ? (
        <Skeleton variant="rounded" height={360} />
      ) : items.length === 0 ? (
        <Alert severity="info">Keine Artikel gefunden. Filter anpassen oder Suchbegriff ändern.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Artikel</TableCell>
                <TableCell>Lieferant</TableCell>
                <TableCell align="right">Letzter Preis</TableCell>
                <TableCell align="right">90 Tg Min</TableCell>
                <TableCell align="right">90 Tg Max</TableCell>
                <TableCell align="right">Δ vs Min</TableCell>
                <TableCell align="right"># Käufe</TableCell>
                <TableCell align="right">Gesamtausgaben</TableCell>
                <TableCell align="center">Warnungen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow
                  key={`${row.supplier}::${row.article_no}`}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openDetail(row)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Art-Nr. {row.article_no} · {row.unit || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.supplier}</TableCell>
                  <TableCell align="right">{fmtEUR(row.last_unit_price)}</TableCell>
                  <TableCell align="right">{fmtEUR(row.min_unit_price_90d)}</TableCell>
                  <TableCell align="right">{fmtEUR(row.max_unit_price_90d)}</TableCell>
                  <TableCell align="right">
                    {row.delta_pct > 0 ? (
                      <Chip size="small" color="warning" label={fmtPct(row.delta_pct)} />
                    ) : (
                      <Typography variant="caption" color="text.secondary">{fmtPct(row.delta_pct)}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{row.line_count}</TableCell>
                  <TableCell align="right">{fmtEUR(row.total_spend)}</TableCell>
                  <TableCell align="center">
                    {row.open_alerts > 0
                      ? <Chip size="small" color="warning" label={row.open_alerts} />
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[25, 50, 100, 200]}
            labelRowsPerPage="Pro Seite:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
          />
        </TableContainer>
      )}

      {/* Detail dialog */}
      <Dialog open={detailOpen} onClose={closeDetail} fullWidth maxWidth="md">
        <DialogTitle>
          {detail ? detail.name : 'Artikeldetails'}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && <Skeleton variant="rounded" height={300} />}
          {detailError && <Alert severity="error">{detailError}</Alert>}
          {detail && !detailLoading && (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Chip size="small" variant="outlined" label={detail.supplier} />
                <Chip size="small" variant="outlined" label={`Art-Nr. ${detail.article_no}`} />
                <Chip size="small" variant="outlined" label={`Einheit ${detail.unit || '—'}`} />
              </Stack>

              {/* KPI cards */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                gap: 2,
                mb: 3,
              }}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="caption" color="text.secondary">Aktueller Preis</Typography>
                  <Typography variant="h6" fontWeight={700}>{fmtEUR(detail.kpi?.current_price)}</Typography>
                </CardContent></Card>
                <Card variant="outlined"><CardContent>
                  <Typography variant="caption" color="text.secondary">90 Tg Min / Max</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {fmtEUR(detail.kpi?.min_90d)} / {fmtEUR(detail.kpi?.max_90d)}
                  </Typography>
                </CardContent></Card>
                <Card variant="outlined"><CardContent>
                  <Typography variant="caption" color="text.secondary">90 Tg Ø</Typography>
                  <Typography variant="h6" fontWeight={700}>{fmtEUR(detail.kpi?.avg_90d)}</Typography>
                </CardContent></Card>
                <Card variant="outlined"><CardContent>
                  <Typography variant="caption" color="text.secondary">Gesamtausgaben</Typography>
                  <Typography variant="h6" fontWeight={700}>{fmtEUR(detail.kpi?.total_spend)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {detail.kpi?.invoice_count} Rechnungen
                  </Typography>
                </CardContent></Card>
              </Box>

              {/* Price history chart */}
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Preisverlauf</Typography>
              {chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Keine Preisdaten verfügbar.
                </Typography>
              ) : (
                <Box sx={{ width: '100%', height: 280, mb: 3 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={(v: number) => v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })}
                        width={90}
                      />
                      <RechartsTooltip
                        formatter={(value: any) => [fmtEUR(Number(value)), `€/${detail.unit || 'Einheit'}`]}
                        labelFormatter={(label: any) => `Datum: ${label}`}
                      />
                      <Line
                        type="monotone"
                        dataKey="unit_price"
                        stroke="#1976d2"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {/* Invoice lines behind the chart (traceability) */}
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Rechnungspositionen ({lineRows.length})
              </Typography>
              {lineRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Noch keine Rechnungspositionen zu diesem Artikel.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Datum</TableCell>
                        <TableCell>Rechnung</TableCell>
                        <TableCell align="right">Menge × Einheit</TableCell>
                        <TableCell align="right">Preis</TableCell>
                        <TableCell align="right">Summe</TableCell>
                        <TableCell align="right">€/Einheit</TableCell>
                        <TableCell align="center" sx={{ width: 44 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineRows.map((p, i) => {
                        // `total` is the line total from the newer backend; `total_eur` is the
                        // legacy field name (merged type) — guard both.
                        const total = p.total ?? (p as { total_eur?: number | null }).total_eur ?? null;
                        const hasPrice = p.unit_price_per_einheit != null;
                        return (
                          <TableRow key={p.line_id ?? `${p.invoice_id}-${i}`} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(p.date)}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography variant="body2">
                                  {p.invoice_no || (p.invoice_id != null ? `#${p.invoice_id}` : '—')}
                                </Typography>
                                <InvoicePdfButton invoiceId={p.invoice_id} invoiceNo={p.invoice_no} onNotice={setPdfNotice} />
                              </Stack>
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {fmtQty(p.quantity)} × {p.unit || detail.unit || '—'}
                            </TableCell>
                            <TableCell align="right">{fmtEUR(p.unit_price)}</TableCell>
                            <TableCell align="right">{fmtEUR(total)}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {hasPrice ? (
                                <Typography variant="body2" fontWeight={600}>
                                  {fmtEURUnit(p.unit_price_per_einheit)}/{p.normalized_unit || 'Einheit'}
                                </Typography>
                              ) : <NichtNormalisiertChip />}
                            </TableCell>
                            <TableCell align="center" sx={{ p: 0 }}>
                              <ProvenanceTooltip line={{ ...p, total, unit: p.unit || detail.unit }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Alerts */}
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Warnungen ({(detail.open_alerts || []).length})
              </Typography>
              {(detail.open_alerts || []).length === 0 ? (
                <Alert severity="success">Keine offenen Warnungen.</Alert>
              ) : (
                <Stack spacing={1}>
                  {(detail.open_alerts || []).map(a => (
                    <Card key={a.id} variant="outlined" sx={{ borderColor: 'warning.main' }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                          <Chip size="small" color="warning" label={fmtPct(a.delta_pct)} />
                          <Chip size="small" variant="outlined" label={a.status} />
                        </Stack>
                        <Typography variant="body2">
                          Aktuell <strong>{fmtEUR(a.current_price)}</strong> · zuletzt günstigster Preis{' '}
                          <strong>{fmtEUR(a.best_price)}</strong> am {fmtDate(a.best_price_date)}
                        </Typography>
                        <Typography variant="body2" color="success.main">
                          Sparpotenzial: <strong>{fmtEUR(a.yearly_impact_eur)}/Jahr</strong>
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!pdfNotice} autoHideDuration={4000} onClose={() => setPdfNotice(null)}
        message={pdfNotice} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
}
